// "My inventions" — the Atlas view (Story #5648 build v2, figure S4
// documentation/…-assets/screen-S4-my-inventions-map.png).
//
// The whole screen, in the figure's order: the how-to-read tip, then a two
// column body — the roster and "Shape at a glance" on the left, the atlas with
// its lens switch, legend, map footer and corpus caveat on the right.
//
// REMOVED in build v2: the stacked "Your inventions" section header with its
// own description and "Review overlap →" link, the separate roster card, the
// separate "Shape at a glance" card and the separate "Shared with you" card.
// The figure has one card on the left and one on the right, and that is what
// this renders — nothing above it, nothing below it.
//
// REAL DATA SOURCES — every row and number below maps to one of these:
//   - GET /projects                                (the caller's own list, with
//     the real document_count, status and shared_with_me)
//   - GET /whitespace/portfolio?scope=own          (entitlement: own ids,
//     granted projects, disclosure-shared sources)
//   - GET /whitespace/portfolio_map?scope=own      (the joint corpus-anchored
//     2-D projection; record ids are {project_id}:{document_id})
//   - GET /whitespace/portfolio_topology?scope=own (validated holes / bridges /
//     frontier edges — the H/B/F markers and three of the tiles)
//   - GET /whitespace/portfolio_score?scope=own    (the discarded-candidate
//     counter behind the "2-D artifacts discarded" tile)
//   - GET /whitespace/portfolio_synergy?scope=own  (cross-project synergy cell
//     count and the scored pairs the amber overlap patches are drawn from)
//   - GET /whitespace/portfolio_shells?scope=own   (the 3-D Explorer lens)
//
// HONEST GAPS vs. the figure — omitted, never faked:
//   - Corpus underlay (the faint grey crosses behind the map): no endpoint
//     returns public-corpus coordinates for a portfolio view.
//   - Shared-in points: portfolio_projection.py excludes shared-in sources from
//     the projected point set, so a shared-in project has no position to plot.
//     The figure lists it as a roster strip and so does this.
import { Fragment, lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getPortfolioEntitlement,
  getPortfolioTopology,
  getPortfolioScore,
  getPortfolioSynergy,
  getPortfolioMap,
  getPortfolioShells,
  getCachedPortfolioOverlap,
  getDisclosureAgreements,
  listAggregatorGrants,
  updateProject,
  getOwnSectors,
  getMarketTaxonomy,
} from '../../services/api'
import {
  buildOwnScopeShapeTiles,
  computeLayoutBounds,
  makeAtlasProjector,
  groupPortfolioLayoutByProject,
  computeCentroid2D,
  computeGroupFootprints,
  projectTagColor,
} from '../../lib/whitespace'
import { useAuth } from '../../contexts/AuthContext'
import { SECTOR_COLORS, UNCLASSIFIED_SECTOR_COLOR, sectorColor } from '../../lib/sectorColors'
import { categoriseSequentially } from '../market/categoriseOwn'
import { statValue } from '../market/marketFormat'
import { COUNTRY_LABELS } from '../../types/market'
import type { MarketCountry } from '../../types/market'
import AtlasHoverCard from './AtlasHoverCard'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import CapFooter, { type CapFooterInvention } from './CapFooter'
import AtlasLayersPanel, {
  DEFAULT_ATLAS_LAYERS,
  type AtlasFilters,
  type AtlasLayerKey,
  type AtlasLayerState,
} from './AtlasLayersPanel'
import OwnInventionRoster from './OwnInventionRoster'
import OwnShapeGlance from './OwnShapeGlance'
import { consentedLevelsByOrg, rollUpSharedInPortfolios } from './sharedInPortfolios'
import {
  F_LABEL_CLEARANCE,
  LEADER_DOT_CLEARANCE,
  LEADER_TARGET_CLEARANCE,
  NAME_LEADER_CAP,
  VIEWPORT,
  NAME_LINE_H,
  NAME_SWATCH,
  boxDistanceTo,
  boxHitsDisc,
  boxHitsEllipse,
  boxesOverlap,
  bridgeControl,
  computeSectorRegions,
  leaderRoute,
  bridgeCurve,
  bridgeEnds,
  frontierArrow,
  labelBoxAt,
  labelWidth,
  placeLabels,
  placeNameChip,
  placePillOnCurve,
  polylineOfQuadratic,
  wrapLabel,
  wrappedLabelSize,
  type Box,
  type Disc,
  type DrawnMarker,
  type Ellipse,
  type Segment,
  type ShapeMarker,
} from './atlasGeometry'
import {
  MIN_ZOOM,
  ZOOM_STEP,
  fit,
  formatZoom,
  labelScale,
  minimapRect,
  pan,
  viewBoxString,
  zoomAt,
  zoomLevel,
  type Viewport,
} from './atlasViewport'
import type { Project } from '../../types'

/**
 * ADDENDUM 8a — NO LEADER IS DRAWN, AND THE ROUTER STAYS ANYWAY.
 *
 * The coordinator's addendum 8a rules that no map in this build may draw a
 * leader: a pale dashed line from a name to a star reads as an eighth ray, or
 * as a box round somebody else's invention. So the render path is gone — there
 * is no <line data-leader-*> and no footer clause describing one.
 *
 * The ROUTING and SCORING below stay, because placeLabels' own scoring consumes
 * them and the placement would move without them. `placeNames` passes
 * `checkLeader: true`, and placeLabels prices a slot outside NAME_LEADER_CAP by
 * whether a clean route could leave it at all (LEADER_BLOCKED / ELBOW_SHAPE /
 * clearance); `score()` below then chooses between the plain and the joint
 * placement by the same measure. Deleting that would not remove ink — it would
 * silently re-place every name on this map, which the coordinator's own
 * condition for this change forbids: serialised before and after, the S4 canvas
 * is byte-for-byte identical, and it is identical precisely because the scoring
 * was left alone.
 *
 * Read the router, then, as what it now is: a REACHABILITY TEST. "Could this
 * slot be joined to its own mark by one clean line?" is a good proxy for "is
 * this slot in the same open space as its mark", which is the figure's own
 * pairing rule — and the answer is used to choose the slot, never to draw
 * anything.
 */

/** An elbow is worth this much less than a straight line of the same clearance
 * when two placements are compared — the figure draws no leaders at all, so the
 * single line is the least foreign shape. */
const ELBOW_PENALTY = 6


/** The area a routed leader may be tested in. Nothing is drawn there any more
 * (see the addendum note above) — a route that would have run along the very
 * edge of the frame is not evidence that a slot sits in open space, so the
 * reachability test keeps its own margin. */
const LEADER_BOUNDS = {
  left: F_LABEL_CLEARANCE,
  top: F_LABEL_CLEARANCE,
  right: VIEWPORT.width - F_LABEL_CLEARANCE,
  bottom: VIEWPORT.height - F_LABEL_CLEARANCE,
}

// three.js must never land in this component's eager chunk — the 3-D lens is
// deferred until it is actually selected, exactly as PortfolioAtlas does it.
const Explorer3D = lazy(() => import('../whitespace/Explorer3D'))

/** Own scope is requested by the query parameter `scope=own` alone — never a
 * user id, sub or email. The server resolves the universe from the verified
 * token. */
const OWN = 'own' as const

/** The country the Sector lens shows sector values for until the reader picks another. */
const DEFAULT_SECTOR_COUNTRY = 'AUS'

/** The mini-map's size in pixels: the drawing's 900 x 560 at 2/15 scale. */
const MINIMAP = { width: 120, height: 75 }

/**
 * Names, marks and strokes are drawn in drawing units, so zooming in would make them grow with
 * the map. This draws its children at the size they have at fit(): scaled about their own anchor
 * by 1/labelScale. At fit() (scale 1) it adds nothing at all — no wrapper, no transform — so the
 * fitted map is byte-for-byte what it was before zoom existed.
 */
function CounterScale({ at, scale, children }: { at: [number, number]; scale: number; children: ReactNode }) {
  if (scale === 1) return <>{children}</>
  const [x, y] = at
  return <g transform={`translate(${x} ${y}) scale(${1 / scale}) translate(${-x} ${-y})`}>{children}</g>
}

type Lens = 'topology' | 'overlap' | 'sector' | '3d'

const LENS_LABEL: Record<Lens, string> = {
  topology: 'Map',
  overlap: 'Overlap',
  sector: 'Sector',
  '3d': '3-D',
}

/** One plain sentence per lens, shown under the switch (UX-023). */
const LENS_HELP: Record<Lens, string> = {
  topology: 'Map: your projects drawn as shapes, with the gaps, bridges and open ground between them.',
  overlap: 'Overlap: where two or more of your projects cover the same ground.',
  sector: 'Sector: your projects coloured by the market sector each one is coded to, with the projects of one sector gathered into a region.',
  '3d': '3-D: the same picture as a space you can turn around and look into.',
}

/**
 * The map call answers 400 "Portfolio has no documents to project" when the reader owns no
 * embedded document yet. That is an empty portfolio, not a failed call, so it gets the same
 * "Nothing is plotted yet" message as a portfolio whose inventions are all empty.
 */
function isEmptyPortfolioError(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: unknown } } | null)?.response
  if (res?.status !== 400) return false
  const body = typeof res.data === 'string' ? res.data : JSON.stringify(res.data ?? '')
  return /no documents to project/i.test(body)
}

export interface MyInventionsAtlasProps {
  /** GET /projects, already held by the screen — never fetched twice. */
  projects: Project[]
}

/** Bug #6682: a footnote names an invention by its name; an id that is not one of the reader's own
 *  projects (a shared-in invention) is never printed raw. */
export function footnoteName(id: string, nameById: Map<string, string>): string {
  return nameById.get(id) ?? 'a shared invention'
}

export default function MyInventionsAtlas({ projects }: MyInventionsAtlasProps) {
  const [lens, setLens] = useState<Lens>('topology')
  // The zoom / pan window (Stream 10a). All the arithmetic lives in atlasViewport.ts; this
  // component only turns pointer and key events into the numbers those functions take.
  const navigate = useNavigate()
  const [viewport, setViewport] = useState<Viewport>(fit())
  const viewportRef = useRef(viewport)
  viewportRef.current = viewport
  const svgRef = useRef<SVGSVGElement | null>(null)
  const dragRef = useRef<{ x: number; y: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  const ls = labelScale(viewport)
  // Layer switches (Stream 10b). Synergy pairs start OFF, as in the figure: they are an extra mark
  // on the map, and the Map lens stays exactly as reviewed until the reader turns them on.
  const [layers, setLayers] = useState<AtlasLayerState>({ ...DEFAULT_ATLAS_LAYERS, synergy: false })
  const layerStyle = (key: AtlasLayerKey) => (layers[key] ? undefined : { display: 'none' as const })

  /** A pointer position as a point in drawing coordinates; the window centre when unmeasured. */
  const toWorld = useCallback((clientX: number, clientY: number): [number, number] => {
    const vp = viewportRef.current
    const r = svgRef.current?.getBoundingClientRect()
    if (!r || r.width === 0 || r.height === 0) return [vp.x + vp.w / 2, vp.y + vp.h / 2]
    return [vp.x + ((clientX - r.left) / r.width) * vp.w, vp.y + ((clientY - r.top) / r.height) * vp.h]
  }, [])
  const zoomBy = useCallback((factor: number, focus?: [number, number]) => {
    setViewport((vp) => zoomAt(vp, factor, focus ?? [vp.x + vp.w / 2, vp.y + vp.h / 2]))
  }, [])
  // The wheel listener is native and non-passive: React's onWheel is passive, and the page must
  // not scroll while the pointer is zooming the map.
  const wheelRef = useRef<(e: WheelEvent) => void>(() => {})
  wheelRef.current = (e: WheelEvent) => {
    e.preventDefault()
    zoomBy(e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP, toWorld(e.clientX, e.clientY))
  }
  const attachSvg = useCallback((el: SVGSVGElement | null) => {
    const onWheel = (e: WheelEvent) => wheelRef.current(e)
    const prev = svgRef.current as (SVGSVGElement & { __atlasWheel?: (e: WheelEvent) => void }) | null
    if (prev?.__atlasWheel) prev.removeEventListener('wheel', prev.__atlasWheel)
    svgRef.current = el
    if (el) {
      ;(el as SVGSVGElement & { __atlasWheel?: (e: WheelEvent) => void }).__atlasWheel = onWheel
      el.addEventListener('wheel', onWheel, { passive: false })
    }
  }, [])
  // The row the reader has PICKED, if any. The figure shows one row highlighted
  // with no shape dimmed, so the highlight defaults to the first invention on
  // the map while dimming stays an explicit act (review round 3, item 7).
  const [pickedId, setPickedId] = useState<string | null>(null)
  // The invention whose Sector-lens card is open. Kept open when the pointer leaves the footprint so the
  // card's link can be reached; cleared by Escape, its close button, leaving the canvas or changing lens.
  const [hoverId, setHoverId] = useState<string | null>(null)
  // Escape closes the card wherever focus is: a user who only hovered has not focused the canvas, so a key
  // handler on the canvas alone never sees the key (found live).
  useEffect(() => {
    if (!hoverId) return undefined
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setHoverId(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [hoverId])
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const entitlementQuery = useQuery({
    queryKey: ['portfolio-entitlement', 'own'],
    queryFn: () => getPortfolioEntitlement(null, OWN),
    retry: false,
  })
  const topologyQuery = useQuery({
    queryKey: ['portfolio-topology', 'own'],
    queryFn: () => getPortfolioTopology(null, OWN),
    retry: false,
  })
  const scoreQuery = useQuery({
    queryKey: ['portfolio-score', 'own'],
    queryFn: () => getPortfolioScore(null, OWN),
    retry: false,
  })
  const synergyQuery = useQuery({
    queryKey: ['portfolio-synergy', 'own'],
    queryFn: () => getPortfolioSynergy(null, false, OWN),
    retry: false,
  })
  const mapQuery = useQuery({
    queryKey: ['portfolio-map', 'own'],
    queryFn: () => getPortfolioMap(null, OWN),
    retry: false,
  })
  const shellsQuery = useQuery({
    queryKey: ['portfolio-shells', 'own'],
    queryFn: () => getPortfolioShells(null, OWN),
    retry: false,
    enabled: lens === '3d',
  })

  const restore = useMutation({
    mutationFn: (projectId: string) => updateProject(projectId, { status: 'active' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['portfolio-map', 'own'] })
    },
  })

  // Owned vs shared-in, by the same rule ProjectsTable and HomeDashboard use.
  const owned = useMemo(
    () => projects.filter((p) => !p.shared_with_me && p.status !== 'archived'),
    [projects],
  )
  const sharedIn = useMemo(() => projects.filter((p) => p.shared_with_me), [projects])
  const archived = useMemo(
    () =>
      projects
        .filter((p) => !p.shared_with_me && p.status === 'archived')
        .map((p) => ({ project_id: p.project_id, name: p.name })),
    [projects],
  )

  // Figure S4's second roster chip: the portfolios the caller has shared their
  // OWN inventions into. Read per project because that is the only route that
  // NAMES the portfolio — /aggregator-grants/projects/{id}/grants carries
  // aggregator_name, its expiry and its folder exclusions. Cached for five
  // minutes like every other read on this page, and only on the Atlas tab.
  const grantQueries = useQueries({
    queries: owned.map((p) => ({
      queryKey: ['aggregator-grants', p.project_id],
      queryFn: () => listAggregatorGrants(p.project_id),
      retry: false,
    })),
  })
  const grantsSettled = grantQueries.every((q) => q.isSuccess || q.isError)
  // The rung a portfolio actually sees, when a disclosure agreement records one
  // — the second of the two places to look (see consentedLevelsByOrg). A grant
  // that names no rung is not proof there is none.
  const agreementsQuery = useQuery({
    queryKey: ['disclosure-agreements', 'own-scope'],
    queryFn: () => getDisclosureAgreements(),
    retry: false,
  })
  const sharedInPortfolios = useMemo(
    () =>
      rollUpSharedInPortfolios(
        owned.map((p, i) => ({ projectId: p.project_id, grants: grantQueries[i]?.data?.grants ?? [] })),
        consentedLevelsByOrg(agreementsQuery.data),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owned, grantQueries.map((q) => q.dataUpdatedAt).join(','), agreementsQuery.data],
  )


  // Which tiles are still waiting on a request (UX-058). A tile is "Computing…"
  // only while the request that fills it is in flight; a settled request with no
  // number stays "Not computed yet".
  const computingTiles = useMemo(() => {
    const set = new Set<string>()
    if (topologyQuery.isLoading) ['Holes', 'Bridges', 'Open ground'].forEach((l) => set.add(l))
    if (synergyQuery.isLoading) set.add('Synergy cells')
    if (topologyQuery.isLoading || scoreQuery.isLoading) set.add('Left off the map')
    return set
  }, [topologyQuery.isLoading, scoreQuery.isLoading, synergyQuery.isLoading])

  const ownedIds = useMemo(() => owned.map((p) => p.project_id), [owned])
  const layout = useMemo(() => mapQuery.data?.layout ?? {}, [mapQuery.data])
  // The server's member list, intersected with what the caller OWNS. Even if a
  // granted project ever reached the layout it is not one of "your inventions"
  // and is not drawn — the screen says so and this enforces it.
  const allPlottedIds = useMemo(
    () => (mapQuery.data?.member_project_ids ?? []).filter((id) => ownedIds.includes(id)),
    [mapQuery.data, ownedIds],
  )

  // ── Sector lens data, and the filters that narrow what it DRAWS (Streams 9 and 10c) ──────────
  // Declared before plottedIds because the filters narrow it. They narrow plottedIds ONLY: what the
  // reader owns, what the map could not place and the shape tiles keep counting allPlottedIds.
  const [sectorCountry, setSectorCountry] = useState(DEFAULT_SECTOR_COUNTRY)
  const sectorsQuery = useQuery({
    queryKey: ['market-sectors', OWN, sectorCountry],
    queryFn: () => getOwnSectors(sectorCountry),
    enabled: lens === 'sector',
    retry: false,
  })
  const taxonomyQuery = useQuery({
    queryKey: ['market-taxonomy'],
    queryFn: getMarketTaxonomy,
    staleTime: Infinity,
    enabled: lens === 'sector',
    retry: false,
  })
  const primarySectionById = useMemo(() => {
    const out: Record<string, string | null> = {}
    const byProject = sectorsQuery.data?.projects ?? {}
    for (const id of allPlottedIds) out[id] = byProject[id]?.sectors.find((s) => s.role === 'primary')?.section ?? null
    return out
  }, [sectorsQuery.data, allPlottedIds])
  // Chips are ticked by default; unticking one HIDES what it stands for.
  const [hiddenSectors, setHiddenSectors] = useState<ReadonlySet<string>>(() => new Set())
  const [hiddenStatuses, setHiddenStatuses] = useState<ReadonlySet<string>>(() => new Set())
  const filteredIds = useMemo(
    () =>
      allPlottedIds.filter((id) => {
        const section = primarySectionById[id]
        if (!section) return !hiddenStatuses.has('unclassified')
        return !hiddenStatuses.has('classified') && !hiddenSectors.has(section)
      }),
    [allPlottedIds, primarySectionById, hiddenSectors, hiddenStatuses],
  )
  // Filters belong to the Sector lens: the Map and Overlap lenses always draw every plotted invention.
  const plottedIds = lens === 'sector' ? filteredIds : allPlottedIds

  const shapeTiles = useMemo(
    () =>
      buildOwnScopeShapeTiles({
        ownedInventionCount: owned.length,
        topology: topologyQuery.data,
        score: scoreQuery.data,
        synergy: synergyQuery.data,
        // Only once the map has answered: before that the count is unknown, not zero.
        mappedOwnedCount: mapQuery.data ? allPlottedIds.length : undefined,
      }),
    [owned.length, topologyQuery.data, scoreQuery.data, synergyQuery.data, mapQuery.data, allPlottedIds],
  )
  const bounds = useMemo(() => computeLayoutBounds(layout), [layout])
  /**
   * WHY THE MAP IS NOT WIDENED (review round 11, item 0 — measured, not assumed).
   *
   * The drawing uses about a third of the canvas width, and widening it would
   * open corridors that would let every name sit beside its own mark with no
   * leader at all. It is NOT a contained change, for two independent reasons:
   *
   * 1. makeAtlasProjector is one uniform fit-to-bounds
   *    (scale = min(drawableW/spanX, drawableH/spanY)) shared by FIVE callers —
   *    PortfolioAtlas, EntityMap, OverlapMiniMap, OwnOverlapMiniMap and this
   *    map. This data is tall and narrow, so height binds and width is left
   *    over. Changing the shared function moves S4b's mini-map, which the
   *    review has scored MATCH and asked to keep still.
   *
   * 2. Wrapping it locally to spread x cannot be done without destroying the
   *    amber shared ground, which is a real intersection of two footprints.
   *    Marine sits at cx 436.8 and Adaptive at cx 372.3 — 64.5px apart, against
   *    a combined x-radius of 90.6, which is why they overlap at all. Reaching
   *    ~82% of the canvas width needs a 1.87x spread, which puts them 120.5px
   *    apart: no intersection, no lens, no "2-way · 50" chip. Scaling the radii
   *    to match would turn every circular footprint into an ellipse, which is a
   *    claim about the shape of an invention that the run never made.
   *
   * So the projection stays as it is, and the placement rules do the work.
   */
  const projector = useMemo(() => makeAtlasProjector(bounds, VIEWPORT), [bounds])
  const pointsByProject = useMemo(
    () => groupPortfolioLayoutByProject(layout, plottedIds),
    [layout, plottedIds],
  )
  // The footer's per-invention point counts describe the whole portfolio, not what the filters show.
  const allPointsByProject = useMemo(
    () => groupPortfolioLayoutByProject(layout, allPlottedIds),
    [layout, allPlottedIds],
  )
  const footprints = useMemo(() => computeGroupFootprints(layout, plottedIds), [layout, plottedIds])
  const footprintById0 = useMemo(() => new Map(footprints.map((f) => [f.project_id, f])), [footprints])
  const footprintIds = useMemo(() => new Set(footprints.map((f) => f.project_id)), [footprints])
  const nameById = useMemo(() => new Map(projects.map((p) => [p.project_id, p.name])), [projects])

  // The figure's H / B / F markers, from the SAME validated topology payload
  // the tiles count. Each candidate carries its own centroid_2d in layout
  // space, so it goes through the same projector as the document dots and lands
  // where the shape actually is. Nothing here is read off the 2-D picture.
  const shapeMarkers = useMemo(() => {
    const t = topologyQuery.data
    if (!t) return [] as ShapeMarker[]
    const rows: ShapeMarker[] = []
    ;(t.holes ?? []).forEach((h, i) =>
      rows.push({ id: `H${i + 1}`, kind: 'hole', at: h.centroid_2d, boundary: h.boundary_record_ids ?? [] }))
    ;(t.bridges ?? []).forEach((b, i) =>
      rows.push({ id: `B${i + 1}`, kind: 'bridge', at: b.centroid_2d, boundary: b.boundary_record_ids ?? [] }))
    ;(t.frontier_edges ?? []).forEach((f, i) =>
      rows.push({ id: `F${i + 1}`, kind: 'frontier', at: f.centroid_2d, boundary: f.boundary_record_ids ?? [] }))
    return rows.filter((r) => Array.isArray(r.at) && r.at.length === 2)
  }, [topologyQuery.data])

  // Where each marker is actually DRAWN. Split out of the render so the drawn
  // count can be compared with the counted one — the atlas may never claim a
  // frontier direction it did not put on the canvas.
  //
  // A BRIDGE IS A CURVE, NOT A RING (review round 1, item 1). The figure draws
  // a hole as a dashed ring around the empty ground and a bridge as a dashed
  // curve joining the two ends of the corridor it is. Both ends come from the
  // run's own boundary records, projected exactly like the document dots, and
  // the curve is bowed so it passes through the bridge's real centroid.
  //
  // A FRONTIER ARROW POINTS OUT (review round 1, item 2). Drawn from the
  // marker's real position along the line away from the middle of the map, so
  // an edge direction reads as one — the previous fixed offsets buried F2 under
  // the footprints in the centre, which is how a counted 7 showed as 6.
  // Everything already ON the canvas, in canvas space — the shapes an arrow head
  // or a name must not be drawn on top of (review round 2, items 2 and 3).
  const drawnFootprints = useMemo<Ellipse[]>(
    () =>
      footprints.map((f) => {
        const [cx, cy] = projector([f.cx, f.cy])
        const [ex] = projector([f.cx + f.rx, f.cy])
        const [, ey] = projector([f.cx, f.cy + f.ry])
        return { key: f.project_id, cx, cy, rx: Math.abs(ex - cx), ry: Math.abs(ey - cy) }
      }),
    [footprints, projector],
  )
  const drawnDiscs = useMemo<Disc[]>(() => {
    const out: Disc[] = []
    for (const id of plottedIds) {
      const pts = pointsByProject.get(id) ?? []
      for (const pt of pts) {
        const [x, y] = projector(pt)
        out.push({ cx: x, cy: y, r: 4 })
      }
      const centre = computeCentroid2D(pts)
      if (centre) {
        const [x, y] = projector(centre)
        out.push({ key: id, cx: x, cy: y, r: 11 })
      }
    }
    return out
  }, [plottedIds, pointsByProject, projector])

  const drawnMarkers = useMemo<DrawnMarker[]>(() => {
    const centre: [number, number] = [VIEWPORT.width / 2, VIEWPORT.height / 2]
    return shapeMarkers.map((mk, index) => {
      const at = projector(mk.at)
      if (mk.kind === 'frontier') {
        const { tail, head, label } = frontierArrow(at, centre, index, VIEWPORT, {
          footprints: drawnFootprints,
          discs: drawnDiscs,
          headRadius: 9,
        })
        return { ...mk, at, tail, head, label }
      }
      if (mk.kind === 'bridge') {
        const ends = bridgeEnds(mk.boundary, layout, projector)
        if (ends) return { ...mk, at, curve: bridgeCurve(ends[0], ends[1], at), ends }
      }
      return { ...mk, at }
    })
  }, [shapeMarkers, projector, layout, drawnFootprints, drawnDiscs])

  // The B/H pills get the same treatment as the names: the B1 pill was sitting
  // on the Adaptive Optics ellipse's edge (review round 2, item 3).
  const markerPills = useMemo(() => {
    const pills: { key: string; x: number; y: number; width: number; height: number }[] = []
    const free: typeof pills = []
    for (const mk of drawnMarkers) {
      if (mk.kind === 'frontier') continue
      // ROUND 4, ITEM 4: a pill that NAMES a curve has to touch that curve. The
      // free 2-D solver had drifted B1 13-19px clear of the bridge it labels,
      // which leaves the corridor unnamed and the pill pointing at nothing. So a
      // bridge's pill is solved along its own curve instead — on it by
      // construction, never merely near it.
      if (mk.kind === 'bridge' && mk.curve && mk.ends) {
        const control = bridgeControl(mk.ends[0], mk.ends[1], mk.at)
        const spot = placePillOnCurve(
          mk.ends[0],
          control,
          mk.ends[1],
          { width: 46, height: 18 },
          { footprints: drawnFootprints, discs: drawnDiscs },
        )
        pills.push({ key: mk.id, x: spot.x, y: spot.y, width: 46, height: 18 })
        continue
      }
      free.push({
        key: mk.id,
        // A hole's pill sits above its ring; a stranded bridge's on its centroid.
        x: mk.at[0],
        y: mk.kind === 'hole' ? mk.at[1] - 22 : mk.at[1] + 4,
        width: 46,
        height: 18,
      })
    }
    const placed = placeLabels(free, {
      footprints: drawnFootprints,
      discs: drawnDiscs,
      boxes: pills.map((p) => labelBoxAt(p)),
    })
    return [...pills, ...free.map((w) => ({ ...w, ...(placed.get(w.key) ?? { x: w.x, y: w.y }) }))]
  }, [drawnMarkers, drawnFootprints, drawnDiscs])
  const markerPillY = useMemo(
    () => new Map(markerPills.map((p) => [p.key, p.y])),
    [markerPills],
  )
  const markerPillX = useMemo(
    () => new Map(markerPills.map((p) => [p.key, p.x])),
    [markerPills],
  )

  // Everything ALREADY drawn as a rectangle: the B/H pills and the F labels.
  // Both are anchored to real geometry, so a NAME moves out of their way rather
  // than the other way round (review round 3, items 1 and 2).
  const drawnBoxes = useMemo<Box[]>(() => markerPills.map((p) => labelBoxAt(p)), [markerPills])

  // Every line the map has already drawn: the seven rays, and the bridge curve
  // as the polyline it is painted as. A name across the bridge is the same
  // fault as a name across a ray, and a LEADER that crosses either is the one
  // line-crossing round 7 found on the whole map. Hoisted out of the name pass
  // because the leader solver needs it too, and it must be the same list.
  // THE FIGURE'S AMBER SHARED GROUND, from the run that actually decides it.
  //
  // Round 6 charged this screen with shading nothing while its own Overlap tab
  // named Marine and Adaptive as one scored group and shaded exactly those two
  // shapes. The cause was the SOURCE: the patches were read from the synergy
  // matrix's pair_scores, whose pairs on this account all name an invention
  // with no footprint, so the atlas drew nothing and the footer explained it
  // with a reason that was false for the strongest pair. Shared ground is what
  // the OVERLAP run says it is, so it is read from the same cached overlap the
  // mini-map draws — with ?no_enqueue, because opening a map must never start a
  // pair computation.
  const footprintById = footprintById0
  const overlapQuery = useQuery({
    // The SAME cache entry the next-step banner already fills, so opening the
    // Atlas costs no extra request and the two can never disagree about what
    // the overlap run found.
    queryKey: ['portfolio-overlap', 'own', 'cached'],
    queryFn: () => getCachedPortfolioOverlap(OWN),
    retry: false,
  })
  const overlapGroups = useMemo(() => overlapQuery.data?.groups ?? [], [overlapQuery.data])
  // Every pair of a group's members that BOTH have a footprint here: those are
  // the two shapes there is ground to shade between. A group also carries its
  // own arity, so the chip can say "2-way"/"3-way" truthfully even when only
  // some of its members are drawn.
  const overlapPatches = useMemo(
    () =>
      overlapGroups.flatMap((group) => {
        const drawn = group.member_project_ids
          .map((id) => footprintById.get(id))
          .filter((f): f is NonNullable<typeof f> => f !== undefined)
        const out: { key: string; a: typeof drawn[0]; b: typeof drawn[0]; score: number; ways: number }[] = []
        for (let i = 0; i < drawn.length; i += 1) {
          for (let j = i + 1; j < drawn.length; j += 1) {
            out.push({
              key: `${group.group_id}:${drawn[i].project_id}:${drawn[j].project_id}`,
              a: drawn[i],
              b: drawn[j],
              score: group.score,
              ways: group.member_project_ids.length,
            })
          }
        }
        return out
      }),
    [overlapGroups, footprintById],
  )

  // THE RAYS — a hard no for anything drawn later. Kept apart from the bridge,
  // which is the one line a leader may cross (coordinator ruling, round 10).
  const drawnLines = useMemo<Segment[]>(
    () =>
      drawnMarkers
        .filter((mk) => mk.kind === 'frontier' && mk.head)
        .map((mk) => ({ a: (mk.tail ?? mk.at) as [number, number], b: mk.head as [number, number] })),
    [drawnMarkers],
  )

  /** The bridge as the polyline it is painted as, its two ends, and the ground
   * a crossing must stay clear of: the amber lens and its score chip. */
  const bridgeLine = useMemo(() => {
    const mk = drawnMarkers.find((m) => m.kind === 'bridge' && m.ends)
    if (!mk || !mk.ends) return undefined
    const ends = mk.ends
    return {
      runs: polylineOfQuadratic(ends[0], bridgeControl(ends[0], ends[1], mk.at), ends[1], 24),
      ends: [ends[0], ends[1]] as [number, number][],
      keepOut: overlapPatches.map((patch) => {
        const [ax, ay] = projector([patch.a.cx, patch.a.cy])
        const [bx, by] = projector([patch.b.cx, patch.b.cy])
        const mx = (ax + bx) / 2
        const my = (ay + by) / 2
        // The chip is 78x20 on that midpoint, and the amber ground sits under
        // it — one box covers both for the purpose of "not near this".
        return { key: `lens-${patch.key}`, left: mx - 45, top: my - 24, right: mx + 45, bottom: my + 24 }
      }),
    }
  }, [drawnMarkers, overlapPatches, projector])

  // Where each invention's name is painted, pulled apart so no two names —
  // and no name and a marker pill — are painted over each other (review round
  // 1, item 6). The x is the shape's own centre; only the y moves.
  /**
   * THE JOINT SOLVE — the names, the frontier labels and the leaders decided
   * together instead of one after another (review round 10).
   *
   * The ordering used to be a trap. Names were placed first, then the frontier
   * labels stepped aside from them, and only then was a leader routed through
   * whatever corridor was left — so the leader inherited a corridor nobody had
   * chosen for it. That produced, in successive rounds, a leader through a
   * label's glyphs, one that boxed a neighbour's name in on three sides, and
   * one that cleared 5px because it was the only shape left. No threshold fixes
   * that; the position and the route have to be scored as one thing.
   *
   * So: place once to learn where the labels want to be, then place the names
   * AGAIN with those labels, arrowheads and rays known — each candidate slot
   * scored by the best leader it could actually leave — then settle the labels
   * against the final names, and only then run the full swept router on the one
   * slot that won.
   *
   * PERFORMANCE, because this is exactly where it goes wrong: the in-loop check
   * uses the CHEAP router (straight plus two plain elbows) and runs only on a
   * candidate that collides with nothing. The swept router costs ~13ms against
   * ~900 candidates per label; putting it in the loop took every atlas test to
   * 8-11 seconds.
   */
  const atlas = useMemo(() => {
    const anchors = new Map<string, [number, number]>()
    const raw = plottedIds
      .map((id) => {
        const centre = computeCentroid2D(pointsByProject.get(id) ?? [])
        if (!centre) return null
        const f = footprintById0.get(id)
        const [x, y] = projector(centre)
        anchors.set(id, [x, y])
        const [, edgeY] = f ? projector([f.cx, f.cy + f.ry]) : [x, y]
        const text = nameById.get(id) ?? id
        // A long real name gets two lines rather than a 290px box no slot on
        // this map can hold. Never truncated — a shortened name cannot be
        // matched back to the roster.
        const lines = wrapLabel(text, 170)
        const { width, height } = wrappedLabelSize(lines)
        return { key: id, x, y: Math.max(y + 22, edgeY + 16), width, height, lines }
      })
      .filter((l): l is NonNullable<typeof l> => l !== null)

    const frontierHeads = new Map<string, [number, number]>(
      drawnMarkers
        .filter((mk) => mk.kind === 'frontier' && mk.head)
        .map((mk) => [mk.id, mk.head as [number, number]]),
    )
    const headBoxes: Box[] = drawnMarkers
      .filter((mk) => mk.kind === 'frontier' && mk.head)
      .map((mk) => {
        const [hx, hy] = mk.head as [number, number]
        // The arrowhead is a triangle about 7px on a side. An 18px box round it
        // was two and a half times its real ink, and it was the thing refusing
        // every straight leader on this map — so the box is the mark's actual
        // size, and the room beyond it is scored rather than demanded.
        return { key: `${mk.id}-head`, left: hx - 5, top: hy - 5, right: hx + 5, bottom: hy + 5 }
      })

    /** Place the invention names, optionally knowing the label ink. */
    const placeNames = (leaderInk?: Box[], siblings: Box[] = []) => {
      const placed = placeLabels(raw, {
        footprints: drawnFootprints,
        discs: drawnDiscs,
        segments: drawnLines,
        bridge: bridgeLine,
        // A NAME may not be drawn across the bridge, even though a LEADER may
        // cross it once: a block of text over a dashed corridor is unreadable
        // either way round.
        boxSegments: [...drawnLines, ...(bridgeLine?.runs ?? [])],
        // The pills, and — on the joint pass — where every OTHER name is going
        // to be. Scoring a slot against only the names placed BEFORE it is what
        // let one be chosen whose leader a later name then sat across.
        boxes: [...drawnBoxes, ...siblings],
        anchors,
        cap: NAME_LEADER_CAP,
        // ALWAYS leader-aware. Conflating "score the leader" with "the label
        // ink is known" is what moved the name to the far side of a footprint
        // on the first pass: with no leader term at all the placer picked a
        // slot no leader could ever leave, and the joint pass then had nothing
        // better to compare against.
        checkLeader: true,
        segmentPad: F_LABEL_CLEARANCE,
        edgePad: F_LABEL_CLEARANCE,
        leaderClearDots: LEADER_DOT_CLEARANCE,
        leaderBounds: LEADER_BOUNDS,
        leaderInk,
      })
      return raw.map((l) => {
        const at = placed.get(l.key) ?? { x: l.x, y: l.y }
        const anchor = anchors.get(l.key)
        const box = labelBoxAt({ ...l, x: at.x }, at.y)
        const needsLeader = anchor !== undefined && boxDistanceTo(box, anchor) > NAME_LEADER_CAP
        return { ...l, ...at, anchor, needsLeader, box }
      })
    }

    /** Route the leader each name needs, against the finished map. */
    const routeLeaders = (
      names: ReturnType<typeof placeNames>,
      leaderInk: Box[],
      opts: { sweep?: boolean; straightOnly?: boolean } = { sweep: true },
    ) =>
      new Map(
        names
          .filter((l) => l.needsLeader && l.anchor)
          .map((l) => {
            const base = {
              footprints: drawnFootprints,
              discs: drawnDiscs,
              segments: drawnLines,
              bridge: bridgeLine,
              boxes: [...drawnBoxes, ...names.map((n) => n.box)],
              leaderBounds: LEADER_BOUNDS,
            }
            // A LEADER THAT EXISTS BEATS A PERFECT ONE THAT DOES NOT. The
            // labels settle after the names, so the ink the final route faces
            // is not quite the ink the placer scored against — and demanding
            // every rule at once can come back with nothing at all, which
            // orphans the name. So the preferences are dropped one at a time,
            // hardest-held last: the 10px from a document dot goes before the
            // ink clearance does, and the rules that stop a leader being drawn
            // ON something, or boxing a neighbour in, are never dropped.
            // LEADER INK IS NEVER RELAXED. Dropping it found a "straight" that
            // was touching the F6 arrowhead — gap 0, measured — which is the
            // round-8 fault wearing the shape the ruling asked for. A leader
            // drawn ON a label or a head is not a leader, whatever its shape.
            // Only the 10px preference from a document dot gives way.
            // ONE SET OF RULES, NONE OF THEM RELAXED (review round 11, item 2).
            // The chain used to drop the 10px from a document dot when nothing
            // else worked, and duly drew a leader 4.9px from one. A name with
            // no clean route now gets NO leader: a missing one is invisible, a
            // compromised one is a line the reader has to interpret.
            const tries = [{ ...base, leaderClearDots: LEADER_DOT_CLEARANCE, leaderInk }]
            // STRAIGHT FIRST, ACROSS THE WHOLE CHAIN. Trying each relaxation
            // to exhaustion before the next one meant an ELBOW under a strict
            // rule beat a STRAIGHT under a slightly looser one — which is the
            // wrong order: the figure's shape is the single line, and the
            // preferences below it are what should give way first.
            for (const straightOnly of [true, false]) {
              for (const obstacles of tries) {
                const route = leaderRoute(
                  l.box,
                  l.anchor as [number, number],
                  l.key,
                  obstacles,
                  F_LABEL_CLEARANCE,
                  { ...opts, straightOnly: straightOnly || opts.straightOnly },
                )
                if (route !== null) return [l.key, route] as const
              }
              if (opts.straightOnly) break
            }
            return [l.key, null] as const
          }),
      )

    /** Place the frontier labels against the names and the reserved corridors. */
    const placeFrontier = (nameBoxes: Box[], reserved: Segment[]) =>
      placeLabels(
        drawnMarkers
          .filter((mk) => mk.kind === 'frontier' && mk.label)
          .map((mk) => ({
            key: mk.id,
            x: (mk.label as [number, number])[0],
            y: (mk.label as [number, number])[1],
            width: labelWidth(mk.id, 10.5),
            height: 11,
          })),
        {
          footprints: drawnFootprints,
          discs: drawnDiscs,
          boxes: [...drawnBoxes, ...nameBoxes],
          // ROUND 5, ITEM 1: an F label 3px from a name, on the same baseline
          // and at the same weight, is read as the last word of that name — the
          // map said "Cold-Chain Vaccine F5". A word space is not clearance.
          boxPad: F_LABEL_CLEARANCE,
          // ROUND 6, ITEM 2: and the label must end up on the same side of
          // every name as the arrowhead it labels, or the two stop reading as
          // one thing. It must also not sit against the frame.
          connectors: frontierHeads,
          edgePad: F_LABEL_CLEARANCE,
          // ROUND 8, ITEM 1: and it keeps off the corridor a leader will need.
          segments: [...drawnLines, ...(bridgeLine?.runs ?? []), ...reserved],
          segmentPad: F_LABEL_CLEARANCE,
        },
        VIEWPORT,
        { dx: [0, -16, 16, -30, 30, -46, 46], dy: [0, -14, 14, -26, 26, -38, 38, -52, 52] },
      )

    const inkFrom = (pos: Map<string, { x: number; y: number }>): Box[] => [
      ...drawnMarkers
        .filter((mk) => mk.kind === 'frontier' && mk.label)
        .map((mk) => {
          const at = pos.get(mk.id) ?? {
            x: (mk.label as [number, number])[0],
            y: (mk.label as [number, number])[1],
          }
          return labelBoxAt({ key: mk.id, x: at.x, y: at.y, width: labelWidth(mk.id, 10.5), height: 11 })
        }),
      ...headBoxes,
    ]

    // A. Where the names go with nothing known about the labels, and B. where
    // the labels then want to be. This pass exists only to learn the ink.
    const names0 = placeNames()
    const reserved0 = [...routeLeaders(names0, headBoxes, { sweep: true, straightOnly: true }).values()]
      .flatMap((r) => r?.runs ?? [])
    const ink0 = inkFrom(placeFrontier(names0.map((n) => n.box), reserved0))

    // C. The joint pass: the names again, each slot scored by the leader it
    // could leave, with that ink known.
    const names1 = placeNames(ink0, names0.map((n) => n.box))

    // D. THE JOINT PASS IS A CANDIDATE, NOT A MANDATE. Scoring position by the
    // leader it can leave is the right idea and it is what this round was for —
    // but on data where NO slot admits a good leader (this map: every straight
    // line from every candidate crosses the bridge or a ray) the extra term has
    // nothing to discriminate on, and it can land the name somewhere worse than
    // the plain placement did. So both are finished and the better one is kept,
    // measured the way the review measures it: a straight beats an elbow, and
    // among routes of a shape more clear space wins.
    const finish = (chosen: ReturnType<typeof placeNames>) => {
      const reserved = [...routeLeaders(chosen, ink0, { sweep: true, straightOnly: true }).values()]
        .flatMap((r) => r?.runs ?? [])
      const frontierLabels = placeFrontier(chosen.map((n) => n.box), reserved)
      // A NAME WITH NO CLEAN ROUTE GETS NOTHING (review round 11, item 2). A
      // missing leader is invisible; a false one is a box drawn round somebody
      // else's invention. Null entries are dropped here rather than rendered as
      // nothing, so `size` means what it says everywhere downstream.
      const routed = routeLeaders(chosen, inkFrom(frontierLabels), { sweep: true })
      const leaders = new Map(
        [...routed.entries()].filter((e): e is [string, NonNullable<(typeof e)[1]>] => e[1] !== null),
      )
      // The frontier labels and arrowheads as BOXES. The leader router already
      // works from this; round 14 needs it too, so a name's colour chip can be
      // held to the same ink the name itself was placed against.
      return { names: chosen, frontierLabels, leaders, labelInk: inkFrom(frontierLabels) }
    }
    const score = (
      leaders: Map<
        string,
        { runs: Segment[]; clearance: number; clearanceOwn: number; crossesBridge: boolean } | null
      >,
    ) => {
      // Fewer leaders is better: the figure draws none at all, so a placement
      // that needs one fewer beats one that clears a little more.
      let total = -Number(leaders.size) * 100
      for (const route of leaders.values()) {
        if (route === null) continue
        total += route.clearance - (route.runs.length > 1 ? ELBOW_PENALTY : 0)
      }
      return total
    }
    const plain = finish(names0)
    // The second placement is only worth computing when the first one left
    // something to improve — a missing leader, an elbow, or one short of the
    // target. On a map where every name sits beside its own mark this costs
    // nothing at all, which is the common case.
    const plainScore = score(plain.leaders)
    if (plainScore >= LEADER_TARGET_CLEARANCE * Math.max(1, plain.leaders.size)) return plain
    const joint = finish(names1)
    return score(joint.leaders) > plainScore ? joint : plain
  }, [
    plottedIds,
    pointsByProject,
    projector,
    footprintById0,
    nameById,
    drawnFootprints,
    drawnDiscs,
    drawnBoxes,
    drawnLines,
    bridgeLine,
    drawnMarkers,
  ])

  const inventionLabels = atlas.names
  const frontierLabelPos = atlas.frontierLabels

  /**
   * ROUND 13/14 — THE COLOUR CHIP FOR A NAME THIS MAP COULD NOT PAIR.
   *
   * The figure's own invariant (round 11, item 1) is that a name sits nearer its
   * OWN star than anybody else's; that is how a reader pairs the two before any
   * leader is drawn. Round 12 then stopped drawing a leader at all when no clean
   * route exists, because a false line is worse than none.
   *
   * That leaves a third state: a name neither beside its mark nor joined to it.
   * On this data exactly one name is in it, and the review measured why no round
   * can fix it by moving things — the free space inside its star's own cell is
   * 37x80px at 8px clearance and the name needs 146x22, while every spot a
   * straight leader could reach is >=199px away inside a neighbour's cell. So
   * the name stays where it is and carries its roster colour instead.
   *
   * WHERE THE CHIP GOES (round 14). Before the first line if it fits, after it
   * if not, and NOWHERE if neither fits — never off the edge of the canvas and
   * never on top of another mark. Both candidates are judged by exactly the same
   * predicate, so there is no "special case" side; preferring the left is the
   * only asymmetry, because that is where a chip reads as belonging to the text
   * that follows it.
   *
   * The chip is measured against the FIRST LINE, not the name's box: a wrapped
   * name's box is as wide as its WIDEST line, so on a narrower line the box edge
   * is empty space and a chip parked there floats away from the text it belongs
   * to. On this fixture that is not cosmetic — anchored on the box the chip
   * lands on the Adaptive footprint's outline; anchored on the first line it
   * clears every footprint by the placer's own 2px margin.
   */
  const nameChips = useMemo(() => {
    const stars = drawnDiscs.filter((d) => d.key !== undefined)
    /** The same ink the NAME was placed against, asked the same way. */
    const clear = (box: Box, ownKey: string) => {
      // A footprint at the placer's own 2px margin — the rule placeLabels
      // prices for a name box. An earlier round approximated this as a ring
      // between rx and rx-2 and duly passed a chip an outline ran through.
      if (drawnFootprints.some((f) => boxHitsEllipse(box, f, 2))) return false
      // A centre star is a mark and stays an obstacle. Document dots are not:
      // the figure already puts names over their own clusters.
      if (stars.some((d) => boxHitsDisc(box, d, 0))) return false
      if (inventionLabels.some((n) => n.key !== ownKey && boxesOverlap(box, n.box))) return false
      if (atlas.labelInk.some((b) => boxesOverlap(box, b))) return false
      if (drawnBoxes.some((b) => boxesOverlap(box, b))) return false
      return true
    }

    const out = new Map<string, { x: number; y: number }>()
    for (const name of inventionLabels) {
      const own = stars.find((s) => s.key === name.key)
      if (!own) continue
      const mine = boxDistanceTo(name.box, [own.cx, own.cy])
      const stray = stars.some(
        (s) => s.key !== name.key && boxDistanceTo(name.box, [s.cx, s.cy]) < mine,
      )
      if (!stray) continue
      // Before the first character, then after the last, then nowhere. The
      // frame test lives in placeNameChip because it is intrinsic; this
      // supplies the ink. Neither slot fitting is a real answer, and the card
      // clause is gated on what comes back rather than on the stray set.
      const slot = placeNameChip(name, (box) => clear(box, name.key))
      if (slot) out.set(name.key, slot)
    }
    return out
  }, [inventionLabels, drawnDiscs, drawnFootprints, drawnBoxes, atlas.labelInk])

  // Groups the map cannot shade, because fewer than two of their members have a
  // footprint here. This is the ONLY case the footer may explain away.
  const groupsWithoutTwoShapes = useMemo(
    () =>
      overlapGroups.filter(
        (group) =>
          group.member_project_ids.filter((id) => footprintById.has(id)).length < 2,
      ).length,
    [overlapGroups, footprintById],
  )
  const withoutFootprint = useMemo(
    () => plottedIds.filter((id) => !footprintIds.has(id)),
    [plottedIds, footprintIds],
  )
  const notDrawn = useMemo(
    () => owned.filter((p) => !allPlottedIds.includes(p.project_id)),
    [owned, allPlottedIds],
  )
  // Bug 6780: "no documents yet" was printed for a project that has a document which simply
  // could not be placed on the map (CAD testing: 1 document). Say which of the two is true.
  const notDrawnNoDocs = useMemo(() => notDrawn.filter((p) => (p.document_count ?? 0) === 0), [notDrawn])
  const notDrawnWithDocs = useMemo(() => notDrawn.filter((p) => (p.document_count ?? 0) > 0), [notDrawn])

  const capInventions = useMemo<CapFooterInvention[]>(
    () =>
      owned.map((p) => ({
        project_id: p.project_id,
        name: p.name,
        document_count: p.document_count ?? 0,
        mapped_point_count: (allPointsByProject.get(p.project_id) ?? []).length,
      })),
    [owned, allPointsByProject],
  )

  // Synergy pairs: the scored pairs from the synergy run whose two inventions are BOTH drawn. A pair
  // that names an invention with no position has nothing to join, so it is not drawn.
  const synergyPairs = useMemo(() => {
    const out: { key: string; a: [number, number]; b: [number, number]; score: number }[] = []
    for (const pair of synergyQuery.data?.pair_scores ?? []) {
      if (pair.overall_score === null) continue
      const ca = computeCentroid2D(pointsByProject.get(pair.project_a_id) ?? [])
      const cb = computeCentroid2D(pointsByProject.get(pair.project_b_id) ?? [])
      if (!ca || !cb) continue
      out.push({ key: pair.pair_key, a: projector(ca), b: projector(cb), score: pair.overall_score })
    }
    return out
  }, [synergyQuery.data, pointsByProject, projector])

  // ── Sector lens (Story #6837 Stream 9) ──────────────────────────────────
  // One colour per invention for the lens being shown: the project tag colour on the
  // 2-D map and overlap lenses (unchanged), the primary sector's colour on the Sector lens.
  const colourFor = useCallback(
    (id: string) => (lens === 'sector' ? sectorColor(primarySectionById[id]) : projectTagColor(id, plottedIds)),
    [lens, primarySectionById, plottedIds],
  )
  // computeSectorRegions works in canvas pixels, so it is fed each drawn invention's projected
  // centre — the same point its star is drawn at. NOT the footprints: an invention with a single
  // plotted document has a dot and a star but no footprint, and must still join its sector's region.
  const sectorRegions = useMemo(() => {
    if (lens !== 'sector') return []
    const centres: Ellipse[] = []
    for (const id of plottedIds) {
      const centre = computeCentroid2D(pointsByProject.get(id) ?? [])
      if (!centre) continue
      const [cx, cy] = projector(centre)
      centres.push({ cx, cy, rx: 0, ry: 0, key: id })
    }
    return computeSectorRegions(centres, primarySectionById)
  }, [lens, plottedIds, pointsByProject, projector, primarySectionById])
  const sectorName = useCallback(
    (section: string) => taxonomyQuery.data?.sections.find((s) => s.code === section)?.name ?? null,
    [taxonomyQuery.data],
  )
  const hoverCard = useMemo(() => {
    if (lens !== 'sector' || !hoverId || !allPlottedIds.includes(hoverId)) return null
    const entry = sectorsQuery.data?.projects[hoverId]
    const primary = entry?.sectors.find((s) => s.role === 'primary') ?? null
    const also = [...new Set((entry?.sectors ?? []).filter((s) => s.role !== 'primary').map((s) => s.section))]
      .filter((s) => s !== primary?.section)
    const ref = primary ? sectorsQuery.data?.reference[primary.section] : undefined
    return {
      projectId: hoverId,
      name: nameById.get(hoverId) ?? hoverId,
      primary: primary ? { section: primary.section, name: sectorName(primary.section) } : null,
      also: also.map((s) => ({ section: s, name: sectorName(s) })),
      value: ref ? `${statValue(ref)} (${ref.year})` : null,
    }
  }, [lens, hoverId, allPlottedIds, sectorsQuery.data, nameById, sectorName])
  // A project with no market_sectors_status has never been categorised.
  const uncategorised = useMemo(
    () => (sectorsQuery.data ? allPlottedIds.filter((id) => !sectorsQuery.data?.projects[id]?.status) : []),
    [sectorsQuery.data, allPlottedIds],
  )
  const [categorising, setCategorising] = useState<{ done: number; total: number; error: string | null } | null>(null)
  const categorise = async () => {
    const result = await categoriseSequentially(uncategorised, (done, total) => setCategorising({ done, total, error: null }))
    await queryClient.invalidateQueries({ queryKey: ['market-sectors', OWN] })
    setCategorising(result.error ? { done: result.done, total: result.total, error: result.error } : null)
  }
  const unclassifiedPlotted = lens === 'sector' && plottedIds.some((id) => !primarySectionById[id])
  const atlasFilters = useMemo<AtlasFilters | undefined>(() => {
    if (lens !== 'sector') return undefined
    const bySection = new Map<string, number>()
    let classified = 0
    for (const id of allPlottedIds) {
      const s = primarySectionById[id]
      if (!s) continue
      classified += 1
      bySection.set(s, (bySection.get(s) ?? 0) + 1)
    }
    const flip = (set: ReadonlySet<string>, key: string) => {
      const next = new Set(set)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    }
    const short = (name: string) => (name.length > 22 ? `${name.slice(0, 21)}…` : name)
    return {
      sectors: [...bySection.keys()].sort().map((s) => {
        const full = taxonomyQuery.data?.sections.find((x) => x.code === s)?.name
        return { key: s, label: full ? `${s} ${short(full)}` : s, title: full ? `${s} ${full}` : undefined, count: bySection.get(s) ?? 0, on: !hiddenSectors.has(s) }
      }),
      onToggleSector: (key) => setHiddenSectors((cur) => flip(cur, key)),
      country: sectorCountry,
      onCountry: setSectorCountry,
      statuses: [
        { key: 'classified', label: 'Classified', count: classified, on: !hiddenStatuses.has('classified') },
        { key: 'unclassified', label: 'Not yet classified', count: allPlottedIds.length - classified, on: !hiddenStatuses.has('unclassified') },
      ],
      onToggleStatus: (key) => setHiddenStatuses((cur) => flip(cur, key)),
      shown: filteredIds.length,
      total: allPlottedIds.length,
      unclassified: allPlottedIds.length - classified,
      onReset: () => {
        setHiddenSectors(new Set())
        setHiddenStatuses(new Set())
      },
    }
  }, [lens, allPlottedIds, primarySectionById, hiddenSectors, hiddenStatuses, sectorCountry, taxonomyQuery.data, filteredIds.length])

  const picked = pickedId && plottedIds.includes(pickedId) ? pickedId : null
  // The highlighted row: the reader's pick, else the first invention the atlas
  // draws — so the roster always shows which invention the map opens on.
  const effectiveFocus = picked ?? plottedIds[0] ?? owned[0]?.project_id ?? null

  return (
    <div data-part="MyInventionsAtlas">
      {/* How to read this — the figure's blue tip above the atlas. */}
      <div className="mb-3 flex items-start gap-2.5 rounded-lg border border-iflow-200 bg-iflow-50/70 px-3 py-2.5 text-[12px] leading-relaxed text-iflow-900">
        <span aria-hidden className="mt-0.5 font-bold text-iflow-600">ℹ</span>
        <p>
          <b>Your inventions, one atlas.</b> Each footprint is one invention you own; the dots are
          its documents and the star its centre. Where two footprints meet, you&apos;re working the
          same ground; holes and bridges are open ground between them. Positions are a reading, not
          a measurement. Other people&apos;s inventions are never drawn here, even when they match
          yours — matches live on each invention&apos;s Matches tab until both sides consent.
          {/* ROUND 13/14: gated on the chips actually RENDERED, not on the
              strays — a name can be unpaired and still get no chip, when
              neither slot beside its first line is clear. And the 3-D lens
              draws no names at all. Either way the clause would be describing
              ink that is not there, which is the rule the footer has followed
              since round 12: never key a mark the map does not draw. */}
          {lens !== '3d' && nameChips.size > 0 && (
            <span data-stray-swatch-clause>
              {' '}
              A name that cannot sit beside its mark carries its colour.
            </span>
          )}
        </p>
      </div>

      <div className="grid items-start gap-3.5 lg:grid-cols-[292px_1fr]">
        {/* LEFT — the roster and the shape tiles, one card, as the figure has it. */}
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
          <OwnInventionRoster
            owned={owned}
            colourScaleIds={plottedIds.length > 0 ? plottedIds : ownedIds}
            colourFor={colourFor}
            sharedIn={sharedIn}
            sharedInSources={entitlementQuery.data?.shared_in_sources}
            sharedInPortfolios={sharedInPortfolios}
            callerUserId={user?.id}
            focusId={effectiveFocus}
            onFocus={(id) => setPickedId((cur) => (cur === id ? null : id))}
          />
          {lens !== '3d' && (
            <div className="mt-3 border-t border-gray-200 pt-3">
              <AtlasLayersPanel
                layers={layers}
                lens={lens}
                filters={atlasFilters}
                onChange={(key, on) => setLayers((cur) => ({ ...cur, [key]: on }))}
              />
            </div>
          )}
          <OwnShapeGlance tiles={shapeTiles} computing={computingTiles} />
        </div>

        {/* RIGHT — the atlas. */}
        <div className="rounded-xl border border-gray-200 bg-white p-3.5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[12.5px] font-bold text-gray-900">
              Atlas <span className="font-normal text-gray-500">· {LENS_LABEL[lens]} lens</span>
            </h3>
            <div role="group" aria-label="Atlas lens" className="inline-flex rounded-lg bg-gray-100 p-0.5">
              {(['topology', 'overlap', 'sector', '3d'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-pressed={lens === l}
                  data-testid={`atlas-lens-${l}`}
                  onClick={() => { setHoverId(null); setLens(l) }}
                  className={`rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                    lens === l ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {LENS_LABEL[l]}
                </button>
              ))}
            </div>
          </div>
          <p data-testid="atlas-lens-help" className="mb-2 text-[12px] text-gray-600">
            {LENS_HELP[lens]}
          </p>
          {lens === 'sector' && sectorsQuery.isError && (
            <div role="alert" data-testid="atlas-sector-error" className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              We could not load the sector view, so the projects are shown uncoloured.{' '}
              <button type="button" onClick={() => void sectorsQuery.refetch()} className="font-semibold underline">Try again</button>
            </div>
          )}
          {lens === 'sector' && allPlottedIds.length > 0 && filteredIds.length === 0 && (
            <div data-testid="atlas-filtered-empty" role="status" className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              Every invention on the map is hidden by the filters.{' '}
              <button type="button" onClick={() => atlasFilters?.onReset()} className="font-semibold underline">Reset filters</button>
            </div>
          )}
          {lens === 'sector' && (uncategorised.length > 0 || categorising !== null) && (
            <div data-testid="atlas-categorise-banner" role="status" className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] text-iflow-900">
              {categorising === null ? (
                <>
                  <span>
                    {uncategorised.length} invention{uncategorised.length === 1 ? '' : 's'} not yet categorised —
                  </span>
                  <button type="button" data-testid="atlas-categorise" onClick={() => void categorise()} className="font-semibold underline">
                    Categorise them
                  </button>
                </>
              ) : (
                <>
                  <span data-testid="atlas-categorise-progress">
                    {categorising.error ? `Stopped after ${categorising.done} of ${categorising.total}: ${categorising.error}` : `Categorising ${Math.min(categorising.done + 1, categorising.total)} of ${categorising.total}…`}
                  </span>
                  {categorising.error && (
                    <button type="button" onClick={() => setCategorising(null)} className="font-semibold underline">Dismiss</button>
                  )}
                </>
              )}
            </div>
          )}

          {mapQuery.isLoading ? (
            <LoadingSkeleton type="card" rows={4} />
          ) : mapQuery.isError && !isEmptyPortfolioError(mapQuery.error) ? (
            <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900">
              <p>We could not load your atlas. Nothing here is out of date — there is simply nothing to draw until it loads.</p>
              <RetryButton onClick={() => void mapQuery.refetch()} />
            </div>
          ) : lens === '3d' ? (
            shellsQuery.isLoading ? (
              <LoadingSkeleton type="card" rows={4} />
            ) : shellsQuery.isError ? (
              <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900">
                <p>We could not load the 3-D view. Nothing here is out of date — it just did not come back.</p>
                <RetryButton onClick={() => void shellsQuery.refetch()} />
              </div>
            ) : (
              <Suspense fallback={<LoadingSkeleton type="card" rows={4} />}>
                <Explorer3D
                  layout={null}
                  overlapShells={shellsQuery.data ?? null}
                  overlapProjectNames={nameById}
                />
              </Suspense>
            )
          ) : allPlottedIds.length === 0 ? (
            <div
              data-testid="atlas-empty"
              className="rounded-lg border border-gray-200 bg-gray-50 p-5 text-center text-[13px] text-gray-700"
            >
              <p className="font-semibold text-gray-900">Your atlas shows how your projects relate.</p>
              <p className="mt-1">Add a document to your first project to start.</p>
              <Link
                to="/upload"
                className="mt-3 inline-flex items-center rounded-lg bg-iflow-700 px-4 py-2 text-sm font-medium text-white hover:bg-iflow-800"
              >
                Add a document
              </Link>
            </div>
          ) : (
            <div
              className="relative"
              onPointerLeave={() => setHoverId(null)}
            >
            <svg
              ref={attachSvg}
              role="img"
              data-testid="own-atlas-svg"
              aria-label="Atlas of your inventions"
              viewBox={viewBoxString(viewport)}
              tabIndex={0}
              style={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'grab' }}
              onPointerDown={(e) => {
                if (e.button !== 0) return
                dragRef.current = { x: e.clientX, y: e.clientY }
                setDragging(true)
                e.currentTarget.setPointerCapture?.(e.pointerId)
              }}
              onPointerMove={(e) => {
                const d = dragRef.current
                if (!d) return
                const r = e.currentTarget.getBoundingClientRect()
                const unitsPerPxX = r.width > 0 ? viewportRef.current.w / r.width : 1
                const unitsPerPxY = r.height > 0 ? viewportRef.current.h / r.height : 1
                const dx = (e.clientX - d.x) * unitsPerPxX
                const dy = (e.clientY - d.y) * unitsPerPxY
                dragRef.current = { x: e.clientX, y: e.clientY }
                setViewport((vp) => pan(vp, -dx, -dy))
              }}
              onPointerUp={() => { dragRef.current = null; setDragging(false) }}
              onPointerCancel={() => { dragRef.current = null; setDragging(false) }}
              onKeyDown={(e) => {
                if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomBy(ZOOM_STEP) }
                else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomBy(1 / ZOOM_STEP) }
                else if (e.key === '0') { e.preventDefault(); setViewport(fit()) }
              }}
              className="block w-full rounded-lg bg-[#F8FBFD] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              <defs>
                {overlapPatches.map((patch) => {
                  const [cx, cy] = projector([patch.a.cx, patch.a.cy])
                  const [ex] = projector([patch.a.cx + patch.a.rx, patch.a.cy])
                  const [, ey] = projector([patch.a.cx, patch.a.cy + patch.a.ry])
                  return (
                    <clipPath key={`clip-${patch.key}`} id={`own-atlas-clip-${cssId(patch.key)}`}>
                      <ellipse cx={cx} cy={cy} rx={Math.abs(ex - cx)} ry={Math.abs(ey - cy)} />
                    </clipPath>
                  )
                })}
                <marker id="own-atlas-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
                  <path d="M0,0 L8,4 L0,8 z" fill="#0369A1" />
                </marker>
              </defs>

              {/* Sector regions — one circle per sector, behind everything else. */}
              {lens === 'sector' && (
                <g data-layer="sectorRegions" style={layerStyle('sectorRegions')}>
                  {sectorRegions.map((region) => {
                    const ref = sectorsQuery.data?.reference[region.section]
                    const name = sectorName(region.section)
                    const label = `${region.section}${name ? ` ${name}` : ''}${ref ? ` · ${statValue(ref)} (${ref.year})` : ''}`
                    // A region can be bigger than the view (one far-off invention stretches it), so
                    // its label is kept inside the visible window instead of above the canvas.
                    const lx = Math.min(Math.max(region.label[0], viewport.x + 80 / ls), viewport.x + viewport.w - 80 / ls)
                    const ly = Math.min(Math.max(region.label[1] - 4, viewport.y + 14 / ls), viewport.y + viewport.h - 6 / ls)
                    return (
                      <g key={`sector-${region.section}`} data-sector-region={region.section}>
                        <ellipse
                          cx={region.cx}
                          cy={region.cy}
                          rx={region.rx}
                          ry={region.ry}
                          fill={sectorColor(region.section)}
                          fillOpacity={0.07}
                          stroke={sectorColor(region.section)}
                          strokeOpacity={0.6}
                          strokeWidth={1.2 / ls}
                          strokeDasharray="5 4"
                        />
                        <CounterScale at={[lx, ly]} scale={ls}>
                        <text
                          x={lx}
                          y={ly}
                          textAnchor="middle"
                          fontSize={10.5}
                          fontWeight={700}
                          fill={sectorColor(region.section)}
                          paintOrder="stroke"
                          stroke="#F8FBFD"
                          strokeWidth={3}
                          strokeLinejoin="round"
                        >
                          {label}
                        </text>
                        </CounterScale>
                      </g>
                    )
                  })}
                </g>
              )}

              {/* Footprints — the figure's primary mark, drawn on both 2-D lenses. */}
              <g data-layer="footprints" style={layerStyle('footprints')}>
              {footprints.map((f) => {
                const [cx, cy] = projector([f.cx, f.cy])
                const [ex] = projector([f.cx + f.rx, f.cy])
                const [, ey] = projector([f.cx, f.cy + f.ry])
                const dim = picked !== null && picked !== f.project_id
                return (
                  <ellipse
                    key={`fp-${f.project_id}`}
                    data-footprint={f.project_id}
                    cx={cx}
                    cy={cy}
                    rx={Math.abs(ex - cx)}
                    ry={Math.abs(ey - cy)}
                    fill={colourFor(f.project_id)}
                    fillOpacity={dim ? 0.05 : lens === 'overlap' ? 0.18 : 0.1}
                    stroke={colourFor(f.project_id)}
                    strokeOpacity={dim ? 0.2 : 0.55}
                    strokeWidth={1.4 / ls}
                    style={{ cursor: 'pointer' }}
                    onPointerEnter={lens === 'sector' ? () => setHoverId(f.project_id) : undefined}
                    onDoubleClick={() => navigate(`/project/${f.project_id}`)}
                  />
                )
              })}
              </g>

              {/* The amber shared ground: the second footprint clipped to the first. */}
              {lens !== 'sector' && (
              <g data-layer="overlap" style={layerStyle('overlap')}>
              {overlapPatches.map((patch) => {
                const [cx, cy] = projector([patch.b.cx, patch.b.cy])
                const [ex] = projector([patch.b.cx + patch.b.rx, patch.b.cy])
                const [, ey] = projector([patch.b.cx, patch.b.cy + patch.b.ry])
                return (
                  <ellipse
                    key={`ov-${patch.key}`}
                    data-overlap-pair={patch.key}
                    cx={cx}
                    cy={cy}
                    rx={Math.abs(ex - cx)}
                    ry={Math.abs(ey - cy)}
                    fill="#FBBF24"
                    fillOpacity={0.55}
                    stroke="#B45309"
                    strokeWidth={1.2}
                    clipPath={`url(#own-atlas-clip-${cssId(patch.key)})`}
                  />
                )
              })}
              </g>
              )}

              {/* Document dots and the invention centre star. */}
              {plottedIds.map((id) => {
                const pts = pointsByProject.get(id) ?? []
                const colour = colourFor(id)
                const centre = computeCentroid2D(pts)
                const name = nameById.get(id) ?? id
                const dim = picked !== null && picked !== id
                return (
                  <g
                    key={id}
                    data-project-id={id}
                    opacity={dim ? 0.35 : 1}
                    onPointerEnter={lens === 'sector' ? () => setHoverId(id) : undefined}
                  >
                    <g data-layer="documents" style={layerStyle('documents')}>
                    {pts.map((pt, i) => {
                      const [x, y] = projector(pt)
                      return <circle key={i} cx={x} cy={y} r={4 / ls} fill={colour} stroke="#fff" strokeWidth={1.2 / ls} />
                    })}
                    </g>
                    {centre && (
                      <CounterScale at={projector(centre)} scale={ls}>
                        <StarMark point={projector(centre)} colour={colour} title={name} />
                      </CounterScale>
                    )}
                  </g>
                )
              })}

              {/* The "2-way · N" chip, on the line between the two centres. The
                  figure draws it on the Topology lens — it used to be hidden
                  there, so even a real patch would have shaded with no number
                  (review round 6, item 1). The arity is the GROUP's, so a group
                  with a member that is not drawn still says what it really is. */}
              {lens !== 'sector' && (
              <g data-layer="overlap" style={layerStyle('overlap')}>
              {overlapPatches.map((patch) => {
                const [ax, ay] = projector([patch.a.cx, patch.a.cy])
                const [bx, by] = projector([patch.b.cx, patch.b.cy])
                const mx = (ax + bx) / 2
                const my = (ay + by) / 2
                return (
                  <g key={`chip-${patch.key}`} data-overlap-chip={patch.key}>
                    <CounterScale at={[mx, my]} scale={ls}>
                      <rect x={mx - 39} y={my - 10} width={78} height={20} rx={10} fill="#FBBF24" stroke="#B45309" />
                      <text x={mx} y={my + 4} textAnchor="middle" fontSize={11} fontWeight={800} fill="#111827">
                        {patch.ways}-way · {patch.score}
                      </text>
                    </CounterScale>
                  </g>
                )
              })}
              </g>
              )}

              {/* Synergy pairs: a thin dashed line between the centres of two inventions the synergy
                  run scored together, with the pair's score. Off until the reader turns it on. */}
              {lens !== 'sector' && (
              <g data-layer="synergy" style={layerStyle('synergy')}>
                {synergyPairs.map((pair) => {
                  const mx = (pair.a[0] + pair.b[0]) / 2
                  const my = (pair.a[1] + pair.b[1]) / 2
                  return (
                    <g key={`syn-${pair.key}`} data-synergy-pair={pair.key}>
                      <line x1={pair.a[0]} y1={pair.a[1]} x2={pair.b[0]} y2={pair.b[1]} stroke="#0D9488" strokeWidth={1.4 / ls} strokeDasharray="2 4" />
                      <CounterScale at={[mx, my]} scale={ls}>
                        <rect x={mx - 26} y={my - 9} width={52} height={18} rx={9} fill="#fff" stroke="#0D9488" />
                        <text x={mx} y={my + 4} textAnchor="middle" fontSize={10.5} fontWeight={700} fill="#0F766E">
                          syn · {pair.score}
                        </text>
                      </CounterScale>
                    </g>
                  )
                })}
              </g>
              )}

              {/* H / B / F markers, from the validated topology payload. A hole
                  is the figure's dashed ring; a bridge is the figure's dashed
                  curve between the two ends of the corridor; a frontier is an
                  arrow pointing out of the set. */}
              <g data-layer="markers" style={layerStyle('markers')}>
              {lens === 'topology' &&
                drawnMarkers.map((mk) => {
                  const [x, y] = mk.at
                  const pillY = markerPillY.get(mk.id) ?? y + 4
                  const pillX = markerPillX.get(mk.id) ?? x
                  if (mk.kind === 'frontier' && mk.head) {
                    const [hx, hy] = mk.head
                    // The ray starts on the outer edge of whatever shape the
                    // marker sits in, never inside it (review round 4, item 2).
                    const [tx, ty] = mk.tail ?? mk.at
                    const placedLabel = frontierLabelPos.get(mk.id)
                    const [lx, ly] = placedLabel
                      ? [placedLabel.x, placedLabel.y]
                      : (mk.label ?? mk.head)
                    return (
                      <g key={`mk-${mk.id}`} data-shape-marker={mk.id} data-marker-kind="frontier">
                        <line
                          x1={tx}
                          y1={ty}
                          x2={hx}
                          y2={hy}
                          stroke="#0369A1"
                          strokeWidth={1.4}
                          strokeDasharray="3 3"
                          markerEnd="url(#own-atlas-arrow)"
                        />
                        <CounterScale at={[lx, ly]} scale={ls}>
                        <text
                          x={lx}
                          y={ly}
                          textAnchor="middle"
                          fontSize={10.5}
                          fontWeight={700}
                          fill="#0369A1"
                          paintOrder="stroke"
                          stroke="#F8FBFD"
                          strokeWidth={3}
                          strokeLinejoin="round"
                        >
                          {mk.id}
                        </text>
                        </CounterScale>
                        <title>Open ground — a direction none of your projects covers yet, checked beyond the 2-D picture</title>
                      </g>
                    )
                  }
                  if (mk.kind === 'bridge' && mk.curve) {
                    return (
                      <g key={`mk-${mk.id}`} data-shape-marker={mk.id} data-marker-kind="bridge">
                        <path
                          d={mk.curve}
                          fill="none"
                          stroke="#0369A1"
                          strokeWidth={1.6}
                          strokeDasharray="6 4"
                        />
                        <CounterScale at={[pillX, pillY]} scale={ls}>
                          <rect x={pillX - 23} y={pillY - 13} width={46} height={18} rx={9} fill="#fff" stroke="#0369A1" />
                          <text x={pillX} y={pillY} textAnchor="middle" fontSize={11} fontWeight={700} fill="#0369A1">
                            {mk.id}
                          </text>
                        </CounterScale>
                        <title>Bridge — a thin corridor joining two parts of your set, checked beyond the 2-D picture</title>
                      </g>
                    )
                  }
                  // A hole, and the honest fallback for a bridge whose two ends
                  // are not both on this map: the ring says where it is without
                  // drawing a corridor between points that were never plotted.
                  return (
                    <g
                      key={`mk-${mk.id}`}
                      data-shape-marker={mk.id}
                      data-marker-kind={mk.kind === 'bridge' ? 'bridge-ring' : 'hole'}
                    >
                      <circle cx={x} cy={y} r={26} fill="none" stroke="#0369A1" strokeWidth={1.5} strokeDasharray="5 4" />
                      <CounterScale at={[pillX, pillY]} scale={ls}>
                        <rect x={pillX - 22} y={pillY - 13} width={44} height={18} rx={9} fill="#fff" stroke="#0369A1" />
                        <text x={pillX} y={pillY} textAnchor="middle" fontSize={11} fontWeight={700} fill="#0369A1">
                          {mk.id}
                        </text>
                      </CounterScale>
                      <title>
                        {mk.kind === 'hole'
                          ? 'Hole — open ground your documents surround, checked beyond the 2-D picture'
                          : 'Bridge — its two ends are not both plotted here, so its position is marked rather than its corridor drawn'}
                      </title>
                    </g>
                  )
                })}
              </g>

              {/* Labels last: a name painted under a marker is as unreadable as
                  no name at all. */}
              {inventionLabels.map(({ key: id, x, y, lines }) => {
                const firstBaseline = lines.length > 1 ? y - NAME_LINE_H : y
                const chip = nameChips.get(id)
                return (
                  <Fragment key={`label-${id}`}>
                    <CounterScale at={[x, firstBaseline]} scale={ls}>
                    {/* ROUND 13/14: a name the map could not seat beside its own
                        mark carries the roster's colour chip, in whichever of
                        the two slots beside its first line is clear. No glyph
                        and no other mark moves by a pixel — the placement this
                        round inherited is the one the review scored. */}
                    {chip && (
                      <rect
                        data-name-swatch={id}
                        x={chip.x}
                        y={chip.y}
                        width={NAME_SWATCH}
                        height={NAME_SWATCH}
                        rx={2}
                        fill={colourFor(id)}
                        stroke="#F8FBFD"
                        strokeWidth={1}
                        pointerEvents="none"
                      />
                    )}
                    <text
                      data-invention-label={id}
                      data-label-lines={lines.length}
                      // Labels sit on top of their own footprint and star; they take no pointer events, so the
                      // hover card (and a double-click to open) reach the invention underneath.
                      pointerEvents="none"
                      x={x}
                      y={firstBaseline}
                      textAnchor="middle"
                      fontSize={11.5}
                      fontWeight={600}
                      fill="#0C4A6E"
                      paintOrder="stroke"
                      stroke="#F8FBFD"
                      strokeWidth={3.5}
                      strokeLinejoin="round"
                    >
                      {lines.map((line, i) => (
                        <tspan key={line} x={x} dy={i === 0 ? 0 : NAME_LINE_H}>
                          {line}
                        </tspan>
                      ))}
                    </text>
                    </CounterScale>
                  </Fragment>
                )
              })}
            </svg>
            {hoverCard && (
              <AtlasHoverCard
                {...hoverCard}
                countryLabel={COUNTRY_LABELS[sectorCountry as MarketCountry] ?? sectorCountry}
                onClose={() => setHoverId(null)}
              />
            )}
            <div data-testid="atlas-zoom-controls" className="absolute right-2 top-2 flex flex-col items-center gap-1">
              <button type="button" aria-label="Zoom in" data-testid="atlas-zoom-in" disabled={zoomLevel(viewport) >= 6} onClick={() => zoomBy(ZOOM_STEP)}
                className="h-7 w-7 rounded-md border border-gray-200 bg-white text-[15px] font-semibold text-iflow-700 shadow-sm disabled:opacity-40">+</button>
              <button type="button" aria-label="Zoom out" data-testid="atlas-zoom-out" disabled={zoomLevel(viewport) <= MIN_ZOOM} onClick={() => zoomBy(1 / ZOOM_STEP)}
                className="h-7 w-7 rounded-md border border-gray-200 bg-white text-[15px] font-semibold text-iflow-700 shadow-sm disabled:opacity-40">−</button>
              <button type="button" aria-label="Fit to view" data-testid="atlas-zoom-fit" onClick={() => setViewport(fit())}
                className="h-7 w-7 rounded-md border border-gray-200 bg-white text-[13px] font-semibold text-iflow-700 shadow-sm">⤢</button>
              <span data-testid="atlas-zoom-readout" className="rounded bg-white/90 px-1 text-[11px] font-semibold text-iflow-700">{formatZoom(viewport)}</span>
            </div>
            <p data-testid="atlas-zoom-hint" className="pointer-events-none absolute bottom-2 left-2 rounded bg-white/85 px-2 py-0.5 text-[11px] text-gray-500">
              scroll to zoom · drag to pan · double-click a footprint to open it
            </p>
            <svg
              data-testid="atlas-minimap"
              aria-hidden
              width={MINIMAP.width}
              height={MINIMAP.height}
              viewBox={`0 0 ${MINIMAP.width} ${MINIMAP.height}`}
              className="absolute bottom-2 right-2 cursor-pointer rounded border border-gray-300 bg-white/90"
              onClick={(e) => {
                // Click the mini-map to centre the window there.
                const r = e.currentTarget.getBoundingClientRect()
                const mx = r.width > 0 ? ((e.clientX - r.left) / r.width) * VIEWPORT.width : VIEWPORT.width / 2
                const my = r.height > 0 ? ((e.clientY - r.top) / r.height) * VIEWPORT.height : VIEWPORT.height / 2
                setViewport((vp) => pan(vp, mx - vp.w / 2 - vp.x, my - vp.h / 2 - vp.y))
              }}
            >
              {footprints.map((f) => {
                const [cx, cy] = projector([f.cx, f.cy])
                const [ex] = projector([f.cx + f.rx, f.cy])
                const [, ey] = projector([f.cx, f.cy + f.ry])
                const k = MINIMAP.width / VIEWPORT.width
                return <ellipse key={`mm-${f.project_id}`} cx={cx * k} cy={cy * k} rx={Math.abs(ex - cx) * k} ry={Math.abs(ey - cy) * k} fill={colourFor(f.project_id)} fillOpacity={0.35} />
              })}
              {(() => {
                const r = minimapRect(viewport, MINIMAP)
                return <rect data-testid="atlas-minimap-window" x={r.x} y={r.y} width={r.width} height={r.height} fill="none" stroke="#0369A1" strokeWidth={1.5} />
              })()}
            </svg>
            </div>
          )}

          {lens !== '3d' && (
            <ul
              aria-label="Atlas legend"
              className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500"
            >
              <li className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-sky-500" aria-hidden /> document
              </li>
              <li>★ invention centre</li>
              <li>◯ footprint</li>
              {lens === 'sector' ? (
                <>
                  {sectorRegions.map((region) => (
                    <li key={`lg-${region.section}`} data-legend-sector={region.section} className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ background: SECTOR_COLORS[region.section as keyof typeof SECTOR_COLORS] }} aria-hidden />
                      {region.section}{sectorName(region.section) ? ` ${sectorName(region.section)}` : ''}
                    </li>
                  ))}
                  {unclassifiedPlotted && (
                    <li data-legend-sector="none" className="flex items-center gap-1.5">
                      <span className="inline-block h-2 w-2 rounded-full" style={{ background: UNCLASSIFIED_SECTOR_COLOR }} aria-hidden /> not categorised
                    </li>
                  )}
                </>
              ) : (
                <>
                  <li>◌ H hole · B bridge · F open ground</li>
                  <li className="flex items-center gap-1.5">
                    <span className="inline-block h-2 w-2 rounded-full bg-amber-400" aria-hidden /> overlap
                  </li>
                </>
              )}
            </ul>
          )}

          <CapFooter
            inventions={capInventions}
            synergyTruncatedProjectIds={synergyQuery.data?.truncated_member_project_ids}
            candidateCount={topologyQuery.data?.candidate_count ?? scoreQuery.data?.candidate_count}
            discardedCandidateCount={
              topologyQuery.data?.discarded_candidate_count ?? scoreQuery.data?.discarded_candidate_count
            }
            archived={archived}
            onRestoreArchived={(id) => restore.mutate(id)}
            noSharedInSources={
              grantsSettled &&
              sharedInPortfolios.length === 0 &&
              (entitlementQuery.data?.shared_in_sources?.length ?? 0) === 0
            }
          />

          {/* What the map could NOT draw. Every clause below is only rendered
              when it is actually true of this run. */}
          {(withoutFootprint.length > 0 ||
            notDrawn.length > 0 ||
            shapeMarkers.length === 0 ||
            overlapPatches.length === 0 ||
            sharedIn.length > 0) && (
            <div className="mt-1 space-y-0.5 text-[12px] text-gray-600">
              {withoutFootprint.length > 0 && (
                <p>
                  {withoutFootprint.length} plotted invention
                  {withoutFootprint.length === 1 ? ' has' : 's have'} a single document each, so they
                  have no measurable footprint — their document dot and centre star are drawn instead
                  of an ellipse: {withoutFootprint.map((id) => footnoteName(id, nameById)).join(', ')}.
                </p>
              )}
              {notDrawnNoDocs.length > 0 && (
                <p>
                  {notDrawnNoDocs.length} of your inventions {notDrawnNoDocs.length === 1 ? 'is' : 'are'} not
                  drawn because {notDrawnNoDocs.length === 1 ? 'it has' : 'they have'} no documents yet:{' '}
                  {notDrawnNoDocs.map((p) => p.name).join(', ')}.
                </p>
              )}
              {notDrawnWithDocs.length > 0 && (
                <p>
                  {notDrawnWithDocs.length} of your inventions {notDrawnWithDocs.length === 1 ? 'has' : 'have'} documents
                  that could not be placed on the map yet, so {notDrawnWithDocs.length === 1 ? 'it is' : 'they are'} not
                  drawn: {notDrawnWithDocs.map((p) => p.name).join(', ')}.
                </p>
              )}
              {shapeMarkers.length === 0 && (
                <p>
                  No hole, bridge or open ground held up under checking, so none is marked on the map.
                </p>
              )}
              {/* An absence is explained ONLY where it is real. Round 6 caught
                  this sentence claiming no pair could be shaded while the map's
                  own two footprints met and the overlap run scored exactly that
                  pair — a footer whose job is honesty printing a false reason.
                  It now speaks only about groups that genuinely have fewer than
                  two shapes here, and says nothing at all when everything the
                  run found is on the map. */}
              {groupsWithoutTwoShapes > 0 && (
                <p>
                  {groupsWithoutTwoShapes} scored group
                  {groupsWithoutTwoShapes === 1 ? ' is' : 's are'} not shaded: fewer than two of
                  {groupsWithoutTwoShapes === 1 ? ' its' : ' their'} inventions have a footprint on
                  this map, and shared ground needs two shapes to sit between.
                </p>
              )}
              {overlapGroups.length === 0 && (
                <p>No shared ground is shaded: the overlap run has not grouped any of your inventions yet.</p>
              )}
              {sharedIn.length > 0 && (
                <p>
                  {sharedIn.length} shared-in project{sharedIn.length === 1 ? ' is' : 's are'} never
                  plotted — a share is not consent to compute across it.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-iflow-700 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-iflow-800"
    >
      <RefreshCw size={13} /> Retry
    </button>
  )
}

/** SVG ids may not contain the '#' a pair_key carries. */
function cssId(raw: string): string {
  return raw.replace(/[^A-Za-z0-9_-]/g, '-')
}

/** The figure's "★ invention centre". */
function StarMark({
  point,
  colour,
  title,
}: {
  point: [number, number]
  colour: string
  title: string
}) {
  const [cx, cy] = point
  const outer = 11
  const inner = 4.6
  const pts: string[] = []
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? outer : inner
    const a = (Math.PI / 5) * i - Math.PI / 2
    pts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`)
  }
  return (
    <polygon data-centre-star points={pts.join(' ')} fill={colour} stroke="#ffffff" strokeWidth={1.5}>
      <title>{title}</title>
    </polygon>
  )
}
